import React, { useEffect, useState } from "react";
import { useGetBanksQuery, useVerifyAccountMutation, useLazyVerifyBvnQuery } from "../../redux/services/Wallet";
import BankDropdown from "./BankDropdown";
import { ShieldCheck, Lock } from "lucide-react";

type BankLinkingProps = {
  bankData: {
    accountName: string;
    bankName: string;
    bankCode?: string;
    accountNumber: string;
    bvn: string;
  };
  handleInputChange: (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
      | { target: { name: string; value: string } }
  ) => void;
  onBack?: () => void;
  onNext: () => void;
};

const BankLinking: React.FC<BankLinkingProps> = ({
  bankData,
  handleInputChange,
  onBack,
  onNext,
}) => {
  const [errors, setErrors] = useState({
    accountName: "",
    bankName: "",
    accountNumber: "",
    bvn: "",
  });

  const { data: banks = [], isLoading, isError } = useGetBanksQuery();
  const [
    verifyAccount,
    { data: verifyData, isLoading: verifying, error: verifyError },
  ] = useVerifyAccountMutation();

  // BVN verification — step 1 of wallet setup. Triggered once the BVN is a
  // full 11 digits, not on every keystroke (mirrors the account-number
  // verification below).
  const [
    triggerVerifyBvn,
    { data: bvnResult, isFetching: verifyingBvn, error: bvnResultError, originalArgs: verifiedBvnArgs },
  ] = useLazyVerifyBvnQuery();

  // Only trust a result that belongs to the BVN currently typed — editing the
  // number after a successful check must not keep the old name as verified.
  const isCurrentBvnResult = !!bankData.bvn && verifiedBvnArgs?.bvn === bankData.bvn;
  const bvnData = isCurrentBvnResult ? bvnResult : undefined;
  const bvnError = isCurrentBvnResult ? bvnResultError : undefined;

  useEffect(() => {
    if (bankData.bvn && bankData.bvn.length === 11) {
      triggerVerifyBvn({ bvn: bankData.bvn });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankData.bvn]);

  // The backend returns the BVN holder's name as separate parts
  // (first_name / middle_name / last_name), not a single account_name.
  const bvnInfo = bvnData?.data;
  const bvnConfirmedName =
    (typeof bvnInfo?.account_name === "string" && bvnInfo.account_name) ||
    [bvnInfo?.first_name, bvnInfo?.middle_name, bvnInfo?.last_name]
      .filter((part) => typeof part === "string" && part.trim())
      .join(" ") ||
    null;
  // The company can't proceed past this step until BVN verification has
  // actually succeeded — a confirmed name, with nothing still in flight or
  // failed.
  const isBvnVerified = !!bvnConfirmedName && !verifyingBvn && !bvnError;
  // Errors come back as { error: { message } } — an object. Only ever render
  // a string here; rendering the object itself crashes the page (white screen).
  const bvnErrorData = (bvnError as any)?.data;
  const bvnErrorCandidate =
    bvnErrorData?.error?.message ??
    bvnErrorData?.message ??
    bvnErrorData?.errors?.bvn?.[0] ??
    bvnErrorData?.error;
  const bvnErrorMessage =
    typeof bvnErrorCandidate === "string" && bvnErrorCandidate.trim()
      ? bvnErrorCandidate
      : "We couldn't verify that BVN. Please check the number and try again.";

  useEffect(() => {
    console.log("🧾 bankData changed:", bankData);
  }, [bankData]);
  // 🔹 Validate a single field
  const validateField = (name: string, value: string) => {
    let errorMsg = "";

    switch (name) {
      case "accountName":
        errorMsg = value ? "" : "Account name is required.";
        break;
      case "bankName":
        errorMsg = value ? "" : "Bank name is required.";
        break;
      case "accountNumber":
        if (!value) errorMsg = "Account number is required.";
        else if (!/^\d+$/.test(value))
          errorMsg = "Account number must be numeric.";
        break;
      case "bvn":
        errorMsg = value ? "" : "BVN is required.";
        break;
      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [name]: errorMsg }));
  };

  // 🔹 Trigger validation for just that field on blur
  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    validateField(name, value);
  };

  // 🔹 On change, update field but don’t auto-validate everything
  const handleInputChangeWithValidation = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    handleInputChange(e);
  };

  // 🔹 Form validation status
  const isFormValid =
    bankData.accountName &&
    bankData.bankName &&
    bankData.accountNumber &&
    bankData.bvn &&
    !errors.accountName &&
    !errors.bankName &&
    !errors.accountNumber &&
    !errors.bvn &&
    isBvnVerified;

  // ✅ Auto verify when both are filled and valid
  useEffect(() => {
    const { bankCode, accountNumber } = bankData;

    if (bankCode && accountNumber && accountNumber.length === 10) {
      verifyAccount({ bankCode, accountNumber })
        .unwrap()
        .then((res) => {
          console.log("✅ Verified:", res);
          if (res?.data?.account_name) {
            handleInputChange({
              target: { name: "accountName", value: res.data.account_name },
            });
          }
        })
        .catch((err) => {
          console.error("❌ Verification failed:", err);
          handleInputChange({
            target: { name: "accountName", value: "" },
          });
        });
    }
  }, [bankData.bankCode, bankData.accountNumber]);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-8">
      <div className="mb-8">
        <h2 className="text-[16px] md:text-[20px] font-semibold text-fleetblue mb-2">
          Link a Bank Account
        </h2>
        <p className="text-gray-500 text-[16px] mt-3 border-b border-[#E0E0E0] pb-3">
          Enter your withdrawal account
        </p>
      </div>

      {/* BVN disclosure — regulatory notice shown before any BVN is entered */}
      <section
        aria-labelledby="bvn-notice-title"
        className="mb-8 rounded-xl border border-blue-100 bg-blue-50/60 p-5"
      >
        <div className="flex items-start gap-3">
          <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-fleetblue" aria-hidden="true" />
          <div className="min-w-0 space-y-3">
            <h3 id="bvn-notice-title" className="text-[15px] font-semibold text-fleetdark">
              Why we ask for your BVN
            </h3>
            <p className="text-sm leading-relaxed text-gray-600">
              Under the Central Bank of Nigeria (CBN) Know Your Customer (KYC) rules, every wallet in
              Nigeria must be linked to a verified Bank Verification Number (BVN) or National
              Identification Number (NIN). We use your BVN to meet this requirement. It confirms your
              identity and protects your wallet against fraud and unauthorised access.
            </p>
            <ul className="space-y-2 text-sm leading-relaxed text-gray-600">
              <li className="flex items-start gap-2">
                <Lock className="mt-1 h-3.5 w-3.5 flex-shrink-0 text-fleetblue" aria-hidden="true" />
                <span>
                  Your BVN is checked against the national BVN database, operated by the Nigeria
                  Inter-Bank Settlement System (NIBSS), through our licensed verification partner,
                  which is authorised to process sensitive personal data.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <Lock className="mt-1 h-3.5 w-3.5 flex-shrink-0 text-fleetblue" aria-hidden="true" />
                <span>
                  Fleetrify does not store your BVN. It is sent over an encrypted connection to our
                  partner solely for verification.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <Lock className="mt-1 h-3.5 w-3.5 flex-shrink-0 text-fleetblue" aria-hidden="true" />
                <span>
                  The result is used only to confirm your identity and match your name to your
                  withdrawal account, in line with the Nigeria Data Protection Act 2023.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* ✅ Bank Name (searchable + scrollable) */}
          <BankDropdown
            banks={banks}
            isLoading={isLoading}
            isError={isError}
            bankData={bankData}
            handleInputChange={handleInputChange}
            handleBlur={handleBlur}
            errors={errors}
          />

          {/* Account Number */}
          <div>
            <label className="block text-[16px] font-medium text-fleetdark mb-2">
              Account Number:<span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="accountNumber"
              value={bankData.accountNumber}
              onChange={handleInputChangeWithValidation}
              onBlur={handleBlur}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none"
              placeholder="Enter account number"
            />
            {verifying && (
              <p className="text-blue-500 text-sm mt-1">Verifying account...</p>
            )}

            {errors.accountNumber && (
              <p className="text-red-500 text-sm mt-1">
                {errors.accountNumber}
              </p>
            )}
            {verifyError && (
              <p className="text-red-500 text-sm mt-1">
                Failed to verify account. Please check details.
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Account Name */}
          <div>
            <label className="block text-[16px] font-medium text-fleetdark mb-2">
              Account Name:<span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="accountName"
              value={bankData.accountName}
              onChange={handleInputChangeWithValidation}
              onBlur={handleBlur}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none"
              placeholder="Enter account name"
              readOnly
            />
            {errors.accountName && (
              <p className="text-red-500 text-sm mt-1">{errors.accountName}</p>
            )}
          </div>

          {/* BVN */}
          <div>
            <label className="block text-[16px] font-medium text-fleetdark mb-2">
              BVN:<span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="bvn"
              value={bankData.bvn}
              onChange={handleInputChangeWithValidation}
              onBlur={handleBlur}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none"
              placeholder="Enter your BVN"
            />
            {errors.bvn && (
              <p className="text-red-500 text-sm mt-1">{errors.bvn}</p>
            )}
            {verifyingBvn && (
              <p className="text-blue-500 text-sm mt-2">Verifying BVN...</p>
            )}
            {!verifyingBvn && bvnConfirmedName && (
              <div className="mt-2 p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-800">
                Confirm: <span className="font-semibold">{bvnConfirmedName}</span> — is this correct?
              </div>
            )}
            {!verifyingBvn && bvnError && (
              <p className="text-red-500 text-sm mt-2">{bvnErrorMessage}</p>
            )}
          </div>
        </div>
      </div>

      {/* Buttons */}
      <div className="flex justify-end gap-4 mt-10">
        {onBack && (
          <button
            className="px-4 py-2 bg-transparent text-fleetblue rounded-md flex items-center border border-fleetblue"
            onClick={onBack}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="w-6 h-6 mr-2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 12H5m0 0l7-7m-7 7l7 7"
              />
            </svg>
            Back
          </button>
        )}

        <button
          className={`px-6 py-2 rounded-[8px] flex items-center justify-center ${
            isFormValid
              ? "bg-fleetblue text-white"
              : "bg-fleetinactiveblue text-[#D0D7E7]"
          }`}
          onClick={onNext}
          disabled={!isFormValid}
        >
          Next: Set Pin
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            className="w-6 h-6 ml-2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M5 12h14m0 0l-5-5m5 5l-5 5"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default BankLinking;
